import type { SimCore } from '@/sim/SimCore';
import type { Snapshot, RacerSnapshot } from '@/net/protocol';

/**
 * snapshot.ts — capture the authoritative SimCore into a serialisable Snapshot,
 * and apply a received Snapshot onto a client's prediction SimCore. State is all
 * plain numbers, so this round-trips through JSON (or, later, a binary codec)
 * with no loss. We deliberately shallow-clone the physics/powerup structs so the
 * snapshot can be sent/stored without aliasing the live sim.
 */

export function captureSnapshot(
  sim: SimCore,
  tick: number,
  ack: Record<number, number>,
  margins: Record<number, number> = {},
): Snapshot {
  const racers: RacerSnapshot[] = sim.racers.map((r) => ({
    id: r.id,
    car: { ...r.car.state },
    cy: r.car.y,
    cvy: r.car.vy,
    cair: r.car.airborne,
    chint: r.car.hint,
    ball: { ...r.ball.state },
    bhint: r.ball.hint,
    powerups: { ...r.powerups },
    finished: r.finished,
    finishOrder: r.finishOrder,
    finishTime: r.finishTime,
    stunTimer: r.stunTimer,
    stunSteer: r.stunSteer,
    padBoost: r.padBoost,
    launchMagnet: r.launchMagnet,
    scorpionTimer: r.scorpionTimer,
    scorpionBallTimer: r.scorpionBallTimer,
    vehicle: r.vehicleId,
    carCh: r.carChannel,
    ballCh: r.ballChannel,
  }));
  return {
    tick,
    state: sim.state,
    raceTime: sim.raceTime,
    countdown: sim.countdown,
    goFlash: sim.goFlash,
    ack: { ...ack },
    racers,
    tackle: sim.tackle ? { ...sim.tackle } : null,
    walls: sim.walls.map((w) => ({ ...w })),
    mud: sim.mud.map((m) => ({ ...m })),
    pickups: sim.pickups.map((p) => p.respawn),
    inputs: Object.fromEntries(sim.lastInputs),
    margins: { ...margins },
  };
}

/** Overwrite a prediction sim with authoritative truth. Does NOT touch hidden
 *  accumulators the snapshot can't carry (the item RNG, finish counter) — they
 *  don't affect car/ball PHYSICS, which is what prediction is about; the held
 *  item itself comes straight from the snapshot. */
export function applySnapshot(sim: SimCore, snap: Snapshot): void {
  sim.state = snap.state;
  sim.raceTime = snap.raceTime;
  sim.countdown = snap.countdown;
  sim.goFlash = snap.goFlash;
  sim.tackle = snap.tackle ? { ...snap.tackle } : null;
  sim.walls = (snap.walls ?? []).map((w) => ({ ...w }));
  sim.mud = (snap.mud ?? []).map((m) => ({ ...m }));
  for (const rs of snap.racers) {
    const r = sim.racers.find((x) => x.id === rs.id);
    if (!r) continue;
    Object.assign(r.car.state, rs.car);
    r.car.y = rs.cy;
    r.car.vy = rs.cvy;
    r.car.airborne = rs.cair;
    r.car.hint = rs.chint;
    Object.assign(r.ball.state, rs.ball);
    r.ball.hint = rs.bhint;
    Object.assign(r.powerups, rs.powerups);
    r.finished = rs.finished;
    r.finishOrder = rs.finishOrder;
    r.finishTime = rs.finishTime;
    r.stunTimer = rs.stunTimer;
    r.stunSteer = rs.stunSteer;
    r.padBoost = rs.padBoost ?? 0;
    r.launchMagnet = rs.launchMagnet ?? 0;
    r.scorpionTimer = rs.scorpionTimer ?? 0;
    r.scorpionBallTimer = rs.scorpionBallTimer ?? 0;
    r.vehicleId = rs.vehicle ?? r.vehicleId;
    r.carChannel = rs.carCh ?? -1;
    r.ballChannel = rs.ballCh ?? -1;
  }
  // M117: the wire carries only the two channel indices; the standings-lerp
  // fields (entry/exit arcs + the ball's channel progress) derive from the
  // placed geometry + the synced ball position — refill them NOW so standings
  // are right before any step.
  sim.refreshChannelDerived();
  snap.pickups.forEach((respawn, i) => {
    const p = sim.pickups[i];
    if (p) p.respawn = respawn;
  });
}

// M105: the legacy JSON encodeSnapshot/decodeSnapshot pair is GONE — it
// name-collided with the binary pair in codec.ts, so an auto-import of the
// wrong one type-checked but shipped frames clients discard (flagged since
// M69). The production wire is codec.ts.
